
import React from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  ArrowLeft,
  ArrowRight,
  Leaf,
  Minus,
  Plus,
  ShoppingBag,
  Trash2,
  Utensils,
  Sparkles,
} from "lucide-react";

import { useCart } from "../../components/CartContext";

import "./css/CartScreen.css";

/* IMAGE URL HELPER - SAME AS THEME 9 */
const getImageUrl = (image) => {
  if (!image) return "";

  const imageValue =
    typeof image === "string"
      ? image
      : image.url || image.image || "";

  if (!imageValue) return "";

  if (
    imageValue.startsWith("http://") ||
    imageValue.startsWith("https://")
  ) {
    return imageValue;
  }

  return `${
    imageValue.startsWith("/") ? "" : "/"
  }${imageValue}`;
};

const formatPrice = (price) =>
  Number(price || 0).toFixed(2);

function CartScreen() {
  const navigate = useNavigate();
  const { restaurantId, tableId } = useParams();

  const {
    cart,
    cartCount,
    cartTotal,
    increaseQuantity,
    decreaseQuantity,
    removeFromCart,
    clearCart,
  } = useCart();

  const homePath =
    `/${restaurantId}/table/${tableId}`;

  const handleBackToMenu = () => {
    navigate(`${homePath}#theme10-menu`);
  };

  const handleContinue = () => {
    if (!cart || cart.length === 0) return;

    navigate(`${homePath}/checkout`);
  };

  /* EMPTY CART */
  if (!cart || cart.length === 0) {
    return (
      <main className="theme10-cart-page">
        <section className="theme10-cart-empty">
          <div className="theme10-cart-empty-art">
            <div className="theme10-cart-empty-circle">
              <ShoppingBag size={40} strokeWidth={1.5} />
            </div>

            <span className="theme10-cart-empty-spark theme10-cart-empty-spark-one">
              <Sparkles size={25} />
            </span>

            <span className="theme10-cart-empty-spark theme10-cart-empty-spark-two">
              <Leaf size={27} />
            </span>
          </div>

          <span className="theme10-cart-eyebrow">
            YOUR FOOD JOURNEY
          </span>

          <h1>
            Nothing in your
            <br />
            <em>basket yet!</em>
          </h1>

          <p>
            Your next delicious meal is waiting.
            Explore our menu and add your favourite
            dishes to get started.
          </p>

          <button
            type="button"
            className="theme10-cart-primary-button"
            onClick={handleBackToMenu}
          >
            Explore Our Menu
            <ArrowRight size={18} />
          </button>

          <div className="theme10-cart-empty-bottom">
            <Leaf size={15} />
            Made fresh, just for you
          </div>
        </section>
      </main>
    );
  }

  /* CART WITH ITEMS */
  return (
    <main className="theme10-cart-page">
      {/* PAGE HEADING */}
      <section className="theme10-cart-header">
        <div className="theme10-cart-container">
          <button
            type="button"
            className="theme10-cart-back-button"
            onClick={handleBackToMenu}
          >
            <ArrowLeft size={17} />
            Back to Menu
          </button>

          <div className="theme10-cart-title-row">
            <div className="theme10-cart-title-content">
              <span className="theme10-cart-eyebrow">
                FRESHLY PICKED FOR YOU
              </span>

              <h1>
                Your <em>Basket.</em>
              </h1>

              <p>
                A delicious meal is on its way.
                Review your favourites before checkout.
              </p>
            </div>

            <div className="theme10-cart-table">
              <span>YOUR TABLE</span>
              <strong>{tableId}</strong>
            </div>
          </div>
        </div>

        <div className="theme10-cart-header-decoration" />
      </section>

      {/* CART CONTENT */}
      <section className="theme10-cart-content">
        <div className="theme10-cart-container">
          <div className="theme10-cart-layout">
            {/* CART ITEMS */}
            <div className="theme10-cart-items-section">
              <div className="theme10-cart-items-heading">
                <div className="theme10-cart-items-title">
                  <span className="theme10-cart-count">
                    {cartCount}
                  </span>

                  <div>
                    <span className="theme10-cart-small-label">
                      YOUR SELECTION
                    </span>

                    <h2>Items in your order</h2>
                  </div>
                </div>

                <button
                  type="button"
                  className="theme10-clear-cart"
                  onClick={clearCart}
                >
                  <Trash2 size={15} />
                  Clear all
                </button>
              </div>

              <div className="theme10-cart-items">
                {cart.map((item) => {
                  const image = getImageUrl(item.image);

                  return (
                    <article
                      className="theme10-cart-item"
                      key={item.id}
                    >
                      {/* IMAGE */}
                      <div className="theme10-cart-item-image">
                        {image ? (
                          <img
                            src={image}
                            alt={item.name}
                            loading="lazy"
                          />
                        ) : (
                          <div className="theme10-cart-image-placeholder">
                            <Utensils size={27} />
                          </div>
                        )}
                      </div>

                      {/* ITEM DETAILS */}
                      <div className="theme10-cart-item-details">
                        <span className="theme10-cart-item-category">
                          <Leaf size={12} />
                          {item.categoryName || "OUR KITCHEN"}
                        </span>

                        <h3>{item.name}</h3>

                        {item.description && (
                          <p>{item.description}</p>
                        )}

                        <div className="theme10-cart-item-price">
                          ₹{formatPrice(item.price)}
                          <small> / item</small>
                        </div>
                      </div>

                      {/* ITEM ACTIONS */}
                      <div className="theme10-cart-item-actions">
                        <div className="theme10-quantity-control">
                          <button
                            type="button"
                            onClick={() =>
                              decreaseQuantity(item.id)
                            }
                            aria-label={`Decrease ${item.name}`}
                          >
                            <Minus size={15} />
                          </button>

                          <span>{item.quantity}</span>

                          <button
                            type="button"
                            onClick={() =>
                              increaseQuantity(item.id)
                            }
                            aria-label={`Increase ${item.name}`}
                          >
                            <Plus size={15} />
                          </button>
                        </div>

                        <strong className="theme10-cart-item-total">
                          ₹{formatPrice(
                            Number(item.price) *
                            Number(item.quantity)
                          )}
                        </strong>

                        <button
                          type="button"
                          className="theme10-remove-item"
                          onClick={() =>
                            removeFromCart(item.id)
                          }
                          aria-label={`Remove ${item.name}`}
                        >
                          <Trash2 size={14} />
                          <span>Remove</span>
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>

              <button
                type="button"
                className="theme10-cart-add-more"
                onClick={handleBackToMenu}
              >
                <Plus size={17} />
                Add more deliciousness
                <ArrowRight size={16} />
              </button>
            </div>

            {/* ORDER SUMMARY */}
            <aside className="theme10-cart-summary">
              <div className="theme10-summary-top">
                <div className="theme10-summary-icon">
                  <ShoppingBag size={22} />
                </div>

                <span className="theme10-cart-eyebrow">
                  ALMOST THERE!
                </span>

                <h2>
                  Your <em>Order.</em>
                </h2>

                <p className="theme10-summary-description">
                  Prepared fresh for table {tableId}.
                </p>
              </div>

              <div className="theme10-summary-items">
                {cart.map((item) => (
                  <div
                    className="theme10-summary-item"
                    key={item.id}
                  >
                    <span>
                      <small>{item.quantity}×</small>
                      {item.name}
                    </span>

                    <strong>
                      ₹{formatPrice(
                        Number(item.price) *
                        Number(item.quantity)
                      )}
                    </strong>
                  </div>
                ))}
              </div>

              <div className="theme10-summary-divider" />

              <div className="theme10-summary-row">
                <span>Subtotal</span>
                <strong>
                  ₹{formatPrice(cartTotal)}
                </strong>
              </div>

              <p className="theme10-summary-note">
                Taxes and service charges will be
                calculated at checkout.
              </p>

              <button
                type="button"
                className="theme10-checkout-button"
                onClick={handleContinue}
              >
                Continue to Checkout
                <ArrowRight size={18} />
              </button>

              <button
                type="button"
                className="theme10-summary-menu-button"
                onClick={handleBackToMenu}
              >
                <ArrowLeft size={15} />
                Add More Items
              </button>

              <div className="theme10-summary-bottom">
                <Leaf size={16} />
                Crafted with love & flavour
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* BOTTOM MESSAGE */}
      <section className="theme10-cart-note">
        <div className="theme10-cart-container">
          <span className="theme10-cart-note-icon">
            <Leaf size={19} />
          </span>

          <p>
            Good food, great company, unforgettable
            flavours. Your meal is prepared with care.
          </p>
        </div>
      </section>
    </main>
  );
}

export default CartScreen;
